import { RunElement } from '../../shared/run-element.js';
import { resolveSkinFromAncestors } from '../../shared/resolve-skin-name.js';
// Import run-checkbox to guarantee child element registration before first render
import '../checkbox/run-checkbox.js';
import * as defaultSkin from './skins/checkbox-group.default.js';
import * as wireframeSkin from './skins/checkbox-group.wireframe.js';

// Constructable stylesheets created once per skin
const _defaultSheet = new CSSStyleSheet();
_defaultSheet.replaceSync(defaultSkin.styles);

const _wireframeSheet = new CSSStyleSheet();
_wireframeSheet.replaceSync(wireframeSkin.styles || '');

/** @type {readonly ['default', 'wireframe']} */
export const CHECKBOX_GROUP_SKINS = Object.freeze(['default', 'wireframe']);

/** @type {readonly ['vertical', 'horizontal']} */
export const CHECKBOX_GROUP_ORIENTATIONS = Object.freeze(['vertical', 'horizontal']);

/** @type {readonly ['sm', 'md', 'lg']} */
export const CHECKBOX_GROUP_SIZES = Object.freeze(['sm', 'md', 'lg']);

class RunCheckboxGroup extends RunElement {
  // Private fields
  #internals = null;
  #children = [];
  #selectAll = null;
  #initialValue = [];
  #rendering = false;
  #value = [];
  #customValidityMessage = '';

  static formAssociated = true;
  static componentName = 'CheckboxGroup';
  static version = '1.0.0';
  static category = 'form-compound';
  static layer = 'composite';
  static parts = ['base', 'label', 'children', 'error'];
  static slots = [
    { name: '', description: 'Child run-checkbox components' },
    { name: 'select-all', description: 'Select-all run-checkbox' },
    { name: 'label', description: 'Group label text' },
    { name: 'error', description: 'Validation error message' },
  ];
  static events = [
    { name: 'change', description: 'Fired when any child checkbox state changes' },
    { name: 'value-change', description: 'Fired when group value array changes' },
  ];

  static get properties() {
    return {
      name: { type: String, reflect: true, default: '' },
      disabled: { type: Boolean, reflect: true, default: false },
      readonly: { type: Boolean, reflect: true, default: false },
      required: { type: Boolean, reflect: true, default: false },
      invalid: { type: Boolean, reflect: true, default: false },
      orientation: { type: String, reflect: true, default: 'vertical' },
      size: { type: String, reflect: true, default: 'md' },
      skin: { type: String, reflect: true, default: null },
    };
  }

  // Bound handler for stable identity
  #onChildChange = (event) => {
    // Only handle run-change events from run-checkbox children
    if (event.type !== 'run-change') return;
    const target = event.target;
    if (!target || target.localName !== 'run-checkbox') return;

    // Determine if this is the select-all checkbox
    if (target === this.#selectAll) {
      this.#handleSelectAllToggle();
    } else {
      this.#rebuildValueFromChildren();
    }

    this.#updateSelectAllState();
    this.#updateFormValue();
    this.#updateValidity();

    // Emit group events
    this.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
    this.dispatchEvent(new CustomEvent('value-change', {
      bubbles: true,
      composed: true,
      detail: { value: [...this.#value] },
    }));
  };

  constructor() {
    super();
    this.#internals = this.attachInternals();
  }

  connectedCallback() {
    super.connectedCallback();
    this.setAttribute('role', 'group');
    this.addEventListener('run-change', this.#onChildChange);
    this.#initialValue = [...this.#value];
    this.#updateAria();
  }

  disconnectedCallback() {
    this.removeEventListener('run-change', this.#onChildChange);
    super.disconnectedCallback();
  }

  // --- Value property (manual, not in static properties — it's an array) ---

  get value() { return [...this.#value]; }
  set value(val) {
    const arr = Array.isArray(val) ? val.map(String) : [];
    this.#value = arr;
    // Update children to match
    for (const child of this.#children) {
      child.checked = arr.includes(child.value);
    }
    this.#updateSelectAllState();
    this.#updateFormValue();
    this.#updateValidity();
    this.requestUpdate();
  }

  // --- Form methods ---

  get form() { return this.#internals.form; }
  get validity() { return this.#internals.validity; }
  get validationMessage() { return this.#internals.validationMessage; }
  get willValidate() { return this.#internals.willValidate; }

  checkValidity() { return this.#internals.checkValidity(); }
  reportValidity() { return this.#internals.reportValidity(); }

  setCustomValidity(message) {
    this.#customValidityMessage = message;
    this.#updateValidity();
  }

  // --- Form callbacks ---

  formResetCallback() {
    this.value = [...this.#initialValue];
  }

  formDisabledCallback(disabled) {
    this.disabled = disabled;
  }

  formAssociatedCallback(_form) {
    // No-op — race-condition guard
  }

  // --- Skin rendering ---

  render() {
    return undefined;
  }

  firstUpdated(_changedProperties) {
    super.firstUpdated?.(_changedProperties);
    // Wire slotchange listeners
    const sr = this.shadowRoot;
    const defaultSlot = sr.querySelector('slot:not([name])');
    if (defaultSlot) {
      defaultSlot.addEventListener('slotchange', () => this.#onDefaultSlotChange());
    }
    const selectAllSlot = sr.querySelector('slot[name="select-all"]');
    if (selectAllSlot) {
      selectAllSlot.addEventListener('slotchange', () => this.#onSelectAllSlotChange());
    }

    // Manually trigger initial slot discovery — slotchange for initial content
    // may have already fired before these listeners were attached
    this.#onDefaultSlotChange();
    this.#onSelectAllSlotChange();

    // Initial validity
    this.#updateValidity();
  }

  updated(changedProperties) {
    super.updated?.(changedProperties);
    if (this.#rendering) return;
    this.#rendering = true;

    try {
      const resolvedSkin = this.#resolveSkin();
      if (resolvedSkin === 'wireframe') {
        this.shadowRoot.adoptedStyleSheets = [_wireframeSheet];
        if (wireframeSkin.render) wireframeSkin.render(this);
        else defaultSkin.render(this);
      } else {
        this.shadowRoot.adoptedStyleSheets = [_defaultSheet];
        defaultSkin.render(this);
      }
    } finally {
      this.#rendering = false;
    }

    // Propagate properties if any propagated property changed
    if (changedProperties.has('disabled') || changedProperties.has('readonly') ||
        changedProperties.has('size') || changedProperties.has('name')) {
      this.#propagateToChildren();
    }

    // Validate orientation/size coercion
    if (!CHECKBOX_GROUP_ORIENTATIONS.includes(this.orientation)) {
      this._properties.orientation = 'vertical';
    }
    if (!CHECKBOX_GROUP_SIZES.includes(this.size)) {
      this._properties.size = 'md';
    }

    this.#updateAria();
  }

  // --- Private methods ---

  #resolveSkin() {
    const instanceSkin = this.getAttribute('skin');
    if (instanceSkin) return instanceSkin;
    return resolveSkinFromAncestors(this);
  }

  #onDefaultSlotChange() {
    const slot = this.shadowRoot.querySelector('slot:not([name])');
    if (!slot) return;
    const assigned = slot.assignedElements({ flatten: true });
    this.#children = assigned.filter(n => n.localName === 'run-checkbox');

    this.#propagateToChildren();
    this.#rebuildValueFromChildren();
    this.#updateSelectAllState();
    this.#updateFormValue();
    this.#updateValidity();
  }

  #onSelectAllSlotChange() {
    const slot = this.shadowRoot.querySelector('slot[name="select-all"]');
    if (!slot) return;
    const assigned = slot.assignedElements({ flatten: true });
    this.#selectAll = assigned.find(n => n.localName === 'run-checkbox') || null;

    if (this.#selectAll) {
      // Propagate group properties to select-all
      this.#propagateToChild(this.#selectAll);
      this.#updateSelectAllState();
    }
  }

  #propagateToChildren() {
    for (const child of this.#children) {
      this.#propagateToChild(child);
    }
    if (this.#selectAll) {
      this.#propagateToChild(this.#selectAll);
    }
  }

  #propagateToChild(child) {
    // Group disabled overrides child individual disabled
    if (this.disabled) child.disabled = true;
    // Group readonly overrides child individual readonly
    if (this.readonly) child.readonly = true;
    // Do NOT propagate name to children — the group handles form submission
    // via its own ElementInternals. Propagating name would cause children
    // (also form-associated) to double-submit values.
    // Always propagate size via attribute so CSS :host([size]) selectors work
    if (this.size) child.setAttribute('size', this.size);
  }

  #rebuildValueFromChildren() {
    this.#value = this.#children
      .filter(c => c.checked)
      .map(c => c.value);
  }

  #handleSelectAllToggle() {
    if (!this.#selectAll) return;
    const shouldCheck = this.#selectAll.checked;

    for (const child of this.#children) {
      if (!child.disabled) {
        child.checked = shouldCheck;
      }
    }

    this.#rebuildValueFromChildren();
  }

  #updateSelectAllState() {
    if (!this.#selectAll) return;

    const nonDisabled = this.#children.filter(c => !c.disabled);
    if (nonDisabled.length === 0) {
      // All children disabled — disable select-all
      this.#selectAll.disabled = true;
      this.#selectAll.checked = false;
      this.#selectAll.indeterminate = false;
      return;
    }

    const checkedCount = nonDisabled.filter(c => c.checked).length;

    if (checkedCount === nonDisabled.length) {
      // All checked
      this.#selectAll.checked = true;
      this.#selectAll.indeterminate = false;
    } else if (checkedCount > 0) {
      // Some checked
      this.#selectAll.checked = false;
      this.#selectAll.indeterminate = true;
    } else {
      // None checked
      this.#selectAll.checked = false;
      this.#selectAll.indeterminate = false;
    }
  }

  #updateFormValue() {
    if (this.disabled) {
      this.#internals.setFormValue(null);
      return;
    }

    if (this.#value.length === 0 || !this.name) {
      this.#internals.setFormValue(null);
      return;
    }

    const formData = new FormData();
    for (const val of this.#value) {
      formData.append(this.name, val);
    }
    this.#internals.setFormValue(formData);
  }

  #updateValidity() {
    if (this.#customValidityMessage) {
      this.#internals.setValidity(
        { customError: true },
        this.#customValidityMessage
      );
      return;
    }

    if (this.required && this.#value.length === 0) {
      this.#internals.setValidity(
        { valueMissing: true },
        'Please select at least one option.'
      );
    } else {
      this.#internals.setValidity({});
    }
  }

  #updateAria() {
    // aria-labelledby references the label part in shadow DOM
    const labelEl = this.shadowRoot?.querySelector('[part="label"]');
    if (labelEl) {
      if (!labelEl.id) labelEl.id = `${this.localName}-label-${Date.now()}`;
      this.setAttribute('aria-labelledby', labelEl.id);
    }

    if (this.invalid) {
      this.setAttribute('aria-invalid', 'true');
    } else {
      this.removeAttribute('aria-invalid');
    }

    if (this.required) {
      this.setAttribute('aria-required', 'true');
    } else {
      this.removeAttribute('aria-required');
    }

    if (this.disabled) {
      this.setAttribute('aria-disabled', 'true');
    } else {
      this.removeAttribute('aria-disabled');
    }
  }
}

if (!customElements.get('run-checkbox-group')) {
  customElements.define('run-checkbox-group', RunCheckboxGroup);
}

export { RunCheckboxGroup };
export default RunCheckboxGroup;
